import React from 'react';

import { Box, Button, Dialog, H1, H2, Text, Tooltip } from '@betterment/samba-react';

import TieredRateTableWithNetting from './TieredRateTableWithNetting';

import { BillingPlanDisplayFragment } from '../../../../graphql/fragments/billing_plan_display.generated';
import { BillingRateTiered, TieredFeeCalculationMethod } from '../../../../graphql/schema';
import { TIERED_CALCULATION_LABEL } from '../data';

const ViewTieredRateModal: React.FC<{
  plan: BillingPlanDisplayFragment;
  platformFee: number;
  onClose: () => void;
}> = ({ plan, platformFee, onClose }) => {
  const tierCalculationMethodLabel = (): string => {
    return TIERED_CALCULATION_LABEL[(plan.billingRate as BillingRateTiered).calculationMethod];
  };

  const tierCalculationMethodTooltip = (): string => {
    if (
      plan.billingRate.__typename === 'BillingRateTiered' &&
      plan.billingRate.calculationMethod === TieredFeeCalculationMethod.Cliff
    ) {
      return "If your client's AUM moves into a different tier, their entire AUM is charged at that tier's rate.";
    }
    return 'Each portion of a client’s AUM is charged at the rate for its specific tier.';
  };

  return (
    <Dialog.Content
      css={{
        minWidth: 'container.large',
        maxHeight: 'container.large',
        overflow: 'auto',
        p: 'space.400',
      }}
      data-testid="viewTieredBillingRatesContent"
    >
      <Box css={{ pl: 'space.200', pr: 'space.200' }}>
        <Dialog.Title asChild={true}>
          <H1 variant="title300" css={{ pb: 'space.150' }}>
            {plan.name}
          </H1>
        </Dialog.Title>
        <Dialog.Description asChild={true}>
          <Box>
            <Box css={{ mb: 'space.200' }}>
              <Text variant="text50" color="text.weak">
                Tiered billing plans are calculated on non-fee exempt balances. Standard 401(k) accounts and Betterment
                Cash Reserve accounts are exempt from these fees. Solo 401(k) accounts remain eligible for fees.
              </Text>
            </Box>
            <Box css={{ mb: 'space.300' }}>
              <H2 variant="title100" css={{ mb: 'space.50' }}>
                Tier type
              </H2>
              <Box css={{ display: 'flex', alignItems: 'center' }}>
                <Text>{tierCalculationMethodLabel()}</Text>
                <Tooltip>
                  <Tooltip.Trigger asChild={true}>
                    <Tooltip.IconButton name="InfoCircle" css={{ ml: 'space.50' }} />
                  </Tooltip.Trigger>
                  <Tooltip.Content align="start" side="top">
                    <Text>{tierCalculationMethodTooltip()}</Text>
                  </Tooltip.Content>
                </Tooltip>
              </Box>
            </Box>
            <Box
              css={{
                borderStyle: 'solid',
                borderColor: 'border',
                borderRadius: 'medium',
                borderWidth: 'thin',
                overflow: 'hidden',
              }}
            >
              <TieredRateTableWithNetting plan={plan} platformFee={platformFee} />
            </Box>
          </Box>
        </Dialog.Description>
        <Box css={{ mt: 'space.300' }}>
          <Button data-testid="modal-close-button" variant="solidSecondary" css={{ width: '90px' }} onClick={onClose}>
            Close
          </Button>
        </Box>
      </Box>
    </Dialog.Content>
  );
};

export default ViewTieredRateModal;
